import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, GitCompareArrows, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useClients } from "@/features/clients/useClients";
import type { client } from "@duress/shared";
import { cn } from "@/lib/utils";

function ClientPickerColumn({
  label,
  selected,
  onSelect,
  onClear,
  exclude,
}: {
  label: string;
  selected: client.ClientListItem | null;
  onSelect: (c: client.ClientListItem) => void;
  onClear: () => void;
  exclude: string | null;
}): React.JSX.Element {
  const { data: clients, isLoading } = useClients();
  const [query, setQuery] = useState("");

  const options = useMemo(() => {
    if (!clients) return [];
    const q = query.trim().toLowerCase();
    return clients
      .filter((c) => c.slug !== exclude)
      .filter((c) => (q ? c.name.toLowerCase().includes(q) : true));
  }, [clients, exclude, query]);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>

      {selected ? (
        <div className="flex items-center justify-between rounded-md border border-phoenix-orange/40 bg-phoenix-orange/5 px-3 py-2.5">
          <span className="font-medium text-sm truncate">{selected.name}</span>
          <button
            type="button"
            onClick={onClear}
            className="ml-2 shrink-0 text-xs text-muted-foreground hover:text-foreground"
          >
            Change
          </button>
        </div>
      ) : (
        <div className="rounded-md border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground">
          No client selected
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input
          placeholder="Search clients…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); }}
          className="pl-8 h-8 text-sm"
        />
      </div>

      <ul className="max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border">
        {isLoading ? (
          <li className="px-3 py-4 text-center text-xs text-muted-foreground">Loading…</li>
        ) : options.length === 0 ? (
          <li className="px-3 py-4 text-center text-xs text-muted-foreground">
            No matching clients.
          </li>
        ) : (
          options.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => { onSelect(c); }}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted",
                  selected?.slug === c.slug && "bg-muted font-medium",
                )}
              >
                <span className="truncate">{c.name}</span>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

export function CompareLandingPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: clients } = useClients();
  const [clientA, setClientA] = useState<client.ClientListItem | null>(null);
  const [clientB, setClientB] = useState<client.ClientListItem | null>(null);

  useEffect(() => {
    const preselect = searchParams.get("a");
    if (preselect && clients && clientA === null) {
      const match = clients.find((c) => c.slug === preselect);
      if (match) setClientA(match);
    }
  }, [searchParams, clients, clientA]);

  const canCompare = clientA !== null && clientB !== null;

  const handleCompare = (): void => {
    if (clientA && clientB) {
      navigate(`/compare/${clientA.slug}/${clientB.slug}`);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Compare</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick two clients to compare side by side.
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 max-w-2xl">
        <ClientPickerColumn
          label="Client A"
          selected={clientA}
          onSelect={(c) => {
            setClientA(c);
            if (clientB?.slug === c.slug) setClientB(null);
          }}
          onClear={() => { setClientA(null); }}
          exclude={clientB?.slug ?? null}
        />
        <ClientPickerColumn
          label="Client B"
          selected={clientB}
          onSelect={(c) => {
            setClientB(c);
            if (clientA?.slug === c.slug) setClientA(null);
          }}
          onClear={() => { setClientB(null); }}
          exclude={clientA?.slug ?? null}
        />
      </div>

      <Button
        onClick={handleCompare}
        disabled={!canCompare}
        className="flex items-center gap-2"
      >
        <GitCompareArrows className="h-4 w-4" />
        Compare
        {canCompare ? (
          <ArrowRight className="h-4 w-4" />
        ) : null}
      </Button>
    </div>
  );
}
